import axios from 'axios';
import { getSubdomainFromHostname } from './context/ConfigContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
});

// Store tenant ID for public requests (set by ConfigContext after tenant lookup)
let currentTenantId = null;
export const setApiTenantId = (tenantId) => {
  currentTenantId = tenantId;
};

// Add token and tenant context to requests
api.interceptors.request.use((config) => {
  // Check if it's an admin route
  if (config.url.includes('/admin/')) {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
  } else {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Add tenant context for multi-tenant data isolation
  // This ensures public endpoints return only the current tenant's data
  if (currentTenantId) {
    config.headers['X-Tenant-ID'] = currentTenantId;
  }

  // Also send subdomain for server-side verification
  const subdomain = getSubdomainFromHostname(window.location.hostname);
  if (subdomain) {
    config.headers['X-Tenant-Subdomain'] = subdomain;
  }

  return config;
});

// Handle demo mode errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is a demo mode error
    if (error.response?.status === 403 && error.response?.data?.isDemo) {
      alert(error.response.data.error);
    }
    return Promise.reject(error);
  }
);

// Helper for admin config
const adminConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  }
});

// Auth API
export const register = (userData) => api.post('/register', userData);
export const login = (credentials) => api.post('/login', credentials);

// Properties API
export const getProperties = () => api.get('/properties');
export const getProperty = (id) => api.get(`/properties/${id}`);
export const getPropertyImages = (propertyId) => api.get(`/properties/${propertyId}/images`);
export const getSoldProperties = () => api.get('/properties/sold');

// Loans API
export const getLoans = () => api.get('/loans');
export const getLoan = (id) => api.get(`/loans/${id}`);
export const createLoan = (loanData) => api.post('/loans', loanData);

// Payments API
export const createPayment = (paymentData) => api.post('/payments', paymentData);
export const getPaymentHistory = (loanId) => api.get(`/loans/${loanId}/payments`);

// Admin APIs
export const getAllCustomers = () => api.get('/admin/customers', adminConfig());
export const getAllProperties = () => api.get('/admin/properties', adminConfig());
export const createCustomLoan = (loanData) => api.post('/admin/loans/create-custom', loanData, adminConfig());
// What escrow a NEW loan on this property would be frozen with, computed by the
// same server helper origination uses. The loan-creation form previews the
// buyer's true monthly payment from this; the client never derives escrow from
// a property's figures itself.
export const getPropertyEscrowPreview = (propertyId) =>
  api.get(`/admin/properties/${propertyId}/escrow-preview`, adminConfig());

// Currency formatting helper
export const formatCurrency = (amount) => {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// States API (Public)
export const getStates = () => api.get('/states');

// States API (Admin)
export const getAdminStates = () => api.get('/admin/states');
export const createState = (stateData) => api.post('/admin/states', stateData);
export const updateState = (id, stateData) => api.patch(`/admin/states/${id}`, stateData);
export const deleteState = (id) => api.delete(`/admin/states/${id}`);

export default api;